import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const COLORS = ["hsl(45 93% 47%)", "hsl(142 71% 45%)", "hsl(217 91% 60%)", "hsl(280 65% 60%)", "hsl(0 84% 60%)"];

export default function EdaTab({ id }) {
  const [eda, setEda] = useState(null);
  useEffect(() => {
    endpoints.eda(id).then((r) => setEda(r.data.eda));
    const t = setInterval(() => endpoints.eda(id).then((r) => setEda(r.data.eda)), 4000);
    return () => clearInterval(t);
  }, [id]);

  if (!eda) return <div className="text-sm text-muted-foreground">EDA still computing…</div>;

  return (
    <div className="space-y-10">
      {eda.target_distribution && (
        <Section title="TARGET DISTRIBUTION">
          <div className="h-64 border border-border rounded-sm p-4 bg-card">
            <ResponsiveContainer>
              {eda.target_distribution.type === "histogram" ? (
                <BarChart data={eda.target_distribution.bins.slice(0, -1).map((b, i) => ({ bin: b.toString(), n: eda.target_distribution.counts[i] }))}>
                  <XAxis dataKey="bin" stroke="hsl(0 0% 45%)" fontSize={10} />
                  <YAxis stroke="hsl(0 0% 45%)" fontSize={10} />
                  <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} />
                  <Bar dataKey="n" fill="hsl(45 93% 47%)" />
                </BarChart>
              ) : (
                <BarChart data={eda.target_distribution.labels.map((l, i) => ({ label: l, n: eda.target_distribution.values[i] }))}>
                  <XAxis dataKey="label" stroke="hsl(0 0% 45%)" fontSize={10} />
                  <YAxis stroke="hsl(0 0% 45%)" fontSize={10} />
                  <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} />
                  <Bar dataKey="n">
                    {eda.target_distribution.labels.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </Section>
      )}

      {eda.histograms?.length > 0 && (
        <Section title="NUMERIC DISTRIBUTIONS">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {eda.histograms.map((h) => (
              <div key={h.column} className="border border-border rounded-sm p-4 bg-card h-48" data-testid={`hist-${h.column}`}>
                <div className="text-xs font-mono truncate mb-2">{h.column}</div>
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart data={h.bins.slice(0, -1).map((b, i) => ({ b: b.toFixed(2), n: h.counts[i] }))}>
                    <XAxis dataKey="b" stroke="hsl(0 0% 45%)" fontSize={9} hide />
                    <YAxis stroke="hsl(0 0% 45%)" fontSize={9} width={30} />
                    <Bar dataKey="n" fill="hsl(217 91% 60%)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        </Section>
      )}

      {eda.correlation?.columns?.length > 0 && (
        <Section title="CORRELATION MATRIX">
          <CorrelationHeatmap data={eda.correlation} />
        </Section>
      )}

      {eda.missing?.length > 0 && (
        <Section title="MISSING VALUES">
          <div className="border border-border rounded-sm p-4 bg-card h-64">
            <ResponsiveContainer>
              <BarChart data={eda.missing} layout="vertical">
                <XAxis type="number" stroke="hsl(0 0% 45%)" fontSize={10} />
                <YAxis dataKey="column" type="category" stroke="hsl(0 0% 45%)" fontSize={10} width={120} />
                <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} />
                <Bar dataKey="missing" fill="hsl(0 84% 60%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      )}

      {eda.categorical?.length > 0 && (
        <Section title="CATEGORICAL BREAKDOWN">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {eda.categorical.map((c) => (
              <div key={c.column} className="border border-border rounded-sm p-4 bg-card h-56">
                <div className="text-xs font-mono truncate mb-2">{c.column}</div>
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart data={c.labels.map((l, i) => ({ l, n: c.values[i] }))}>
                    <XAxis dataKey="l" stroke="hsl(0 0% 45%)" fontSize={9} />
                    <YAxis stroke="hsl(0 0% 45%)" fontSize={9} width={30} />
                    <Bar dataKey="n" fill="hsl(142 71% 45%)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="label-mono mb-3">// {title}</div>
      {children}
    </div>
  );
}

function CorrelationHeatmap({ data }) {
  const { columns, matrix } = data;
  const size = 32;
  const color = (v) => {
    const abs = Math.abs(v);
    const hue = v > 0 ? 45 : 217; // amber positive, blue negative
    const light = 20 + abs * 40;
    return `hsl(${hue} 80% ${light}%)`;
  };
  return (
    <div className="border border-border rounded-sm p-5 bg-card overflow-auto">
      <div className="inline-block">
        <div className="flex gap-[2px] ml-24">
          {columns.map((c) => (
            <div key={c} style={{ width: size }} className="text-[9px] font-mono truncate rotate-[-45deg] origin-left h-6 text-muted-foreground">
              {c}
            </div>
          ))}
        </div>
        {matrix.map((row, i) => (
          <div key={i} className="flex items-center gap-[2px] mt-[2px]">
            <div className="w-24 pr-2 text-[10px] font-mono truncate text-right text-muted-foreground">{columns[i]}</div>
            {row.map((v, j) => (
              <div
                key={j}
                title={`${columns[i]} × ${columns[j]}: ${v.toFixed(2)}`}
                style={{ width: size, height: size, background: color(v) }}
                className="grid place-items-center text-[9px] font-mono text-white/80"
              >
                {v.toFixed(1)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
